import { FieldTemplateProps, ObjectFieldTemplateProps, WidgetProps } from '@rjsf/utils';
import { FC, Fragment } from 'react';

const inputClass =
  'w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100 disabled:cursor-not-allowed disabled:bg-gray-100';

const textareaClass =
  'w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100';

const labelClass = 'mb-1 block text-sm font-medium text-gray-800';

const descriptionClass = 'mt-1 text-xs text-gray-500';

const errorClass = 'mt-1 text-xs font-medium text-red-600';

const cardClass = 'rounded-2xl border border-gray-200 bg-white p-4';

export const StyledField: FC<FieldTemplateProps> = (props) => {
  const { id, label, required, description, errors, children, hidden, rawErrors } = props;

  if (hidden) return <>{children}</>;

  return (
    <div className={`space-y-1 ${cardClass}`}>
      {label && (
        <label htmlFor={id} className={labelClass}>
          {label}
          {required && <span className='ml-1 text-red-500'>*</span>}
        </label>
      )}
      {children}
      {description && <div className={descriptionClass}>{description}</div>}
      {rawErrors?.length ? <div className={errorClass}>{errors}</div> : null}
    </div>
  );
};

export const ObjectFieldTemplate: FC<ObjectFieldTemplateProps> = (props) => {
  const { title, description, properties } = props;

  return (
    <div className='space-y-6'>
      {title && (
        <div>
          <h2 className='text-xl font-semibold text-gray-900'>{title}</h2>
          {description && <p className='mt-1 text-sm text-gray-500'>{props.description}</p>}
        </div>
      )}

      <div className='grid grid-cols-1 gap-5 md:grid-cols-2'>
        {properties?.map((property) => {
          const fullWidthFields = ['template_header_text', 'header_examples', 'template_body_text', 'body_examples', 'template_footer_text'];

          const isFullWidth = fullWidthFields.includes(property.name);

          return (
            <div key={property.name} className={isFullWidth ? 'md:col-span-2' : ''}>
              {property.content}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export const TextWidget: FC<WidgetProps> = (props) => {
  return (
    <input
      id={props.id}
      type='text'
      value={props.value || ''}
      placeholder={props.placeholder}
      disabled={props.disabled || props.readonly}
      required={props.required}
      className={inputClass}
      onChange={(event) => props.onChange(event.target.value)}
      onBlur={(event) => props.onBlur(props.id, event.target.value)}
      onFocus={(event) => props.onFocus(props.id, event.target.value)}
    />
  );
};

export const TextareaWidget: FC<WidgetProps> = (props) => {
  const rows = props.options.rows || 5;

  return (
    <textarea
      id={props.id}
      value={props.value || ''}
      placeholder={props.placeholder}
      disabled={props.disabled || props.readonly}
      required={props.required}
      rows={Number(rows)}
      className={textareaClass}
      onChange={(event) => props.onChange(event.target.value)}
      onBlur={(event) => props.onBlur(props.id, event.target.value)}
      onFocus={(event) => props.onFocus(props.id, event.target.value)}
    />
  );
};

export const SelectWidget: FC<WidgetProps> = (props) => {
  return (
    <select
      id={props.id}
      value={props.value || ''}
      disabled={props.disabled || props.readonly}
      required={props.required}
      className={inputClass}
      onChange={(event) => props.onChange(event.target.value)}
      onBlur={(event) => props.onBlur(props.id, event.target.value)}
      onFocus={(event) => props.onFocus(props.id, event.target.value)}
    >
      <option value=''>Select...</option>

      {props.options.enumOptions?.map((option: any) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  );
};

export const RadioWidget: FC<WidgetProps> = (props) => {
  return (
    <div className='grid grid-cols-1 gap-2 sm:grid-cols-2'>
      {props.options.enumOptions?.map((option: any) => {
        const checked = props.value === option.value;

        return (
          <label
            key={option.value}
            className={`!flex cursor-pointer items-center gap-4 rounded-lg border px-4 py-3 text-sm transition ${
              checked ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            <input
              type='radio'
              name={props.id}
              value={option.value}
              checked={checked}
              disabled={props.disabled || props.readonly}
              className='h-4 w-4 border-gray-300 text-blue-600 focus:ring-blue-500'
              onChange={() => props.onChange(option.value)}
            />
            <span className='font-bold'>{option.label}</span>
          </label>
        );
      })}
    </div>
  );
};

export const ArrayFieldTemplate: FC<ArrayFieldTemplateProps> = (props) => {
  return (
    <div className='rounded-xl border border-gray-200 bg-gray-50 p-4'>
      <div className='mb-3 flex items-start justify-between gap-4'>
        <div>
          {props.title && <h3 className='text-sm font-semibold text-gray-900'>{props.title}</h3>}
          {props.schema.description && <p className='mt-1 text-xs text-gray-500'>{props.schema.description}</p>}
        </div>

        {props.canAdd && (
          <button
            type='button'
            onClick={props.onAddClick}
            className='rounded-lg bg-blue-600 px-3 py-2 text-xs font-medium text-white shadow-sm hover:bg-blue-700'
          >
            Add example
          </button>
        )}
      </div>

      <div className='space-y-3'>
        {props.items.map((item: any) => (
          <div key={item.key} className='flex items-start gap-3 rounded-lg border border-gray-200 bg-white p-3'>
            <div className='flex-1'>{item.children}</div>

            {item.hasRemove && (
              <button
                type='button'
                onClick={item.onDropIndexClick(item.index)}
                className='rounded-lg border border-red-200 px-3 py-2 text-xs font-medium text-red-600 hover:bg-red-50'
              >
                Remove
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export const templates = {
  StyledField,
  ObjectFieldTemplate,
  ArrayFieldTemplate,
};

export const widgets = {
  TextWidget,
  TextareaWidget,
  SelectWidget,
  RadioWidget,
};
